/**
 * APPOINTMENT-TIME-ENTRY · Time-input normalisation utilities.
 *
 * The shared web appointment forms accept typed time entry (no
 * native `type="time"` / `datetime-local` pickers). Users can type:
 *
 *   "9"      → "09:00"
 *   "9:00"   → "09:00"
 *   "0900"   → "09:00"
 *   "09"     → "09:00"
 *   "930"    → "09:30"
 *   "23:59"  → "23:59"
 *
 * Anything that cannot be resolved to an hour in [0, 23] and a
 * minute in [0, 59] returns `null` — the caller decides whether
 * that's an inline validation error or a submit-time refusal.
 *
 * `combineLocalDateTimeToIso(dateStr, timeStr)` combines a
 * `YYYY-MM-DD` date input value with an `HH:MM` typed time in the
 * BROWSER's local timezone, then serialises to ISO 8601. This
 * matches the drift-free semantics of the previous
 * `datetime-local` pickers so backend `start_at` / `end_at` payloads
 * remain byte-identical for the same wall clock.
 */

const TIME_HHMM_RE = /^(\d{1,2}):(\d{2})$/;
const TIME_HHONLY_RE = /^(\d{1,2})$/;
const TIME_DIGITS_RE = /^(\d{3,4})$/;

/**
 * @param {string} raw
 * @returns {string|null} canonical "HH:MM" (00:00 .. 23:59) or null when unresolvable
 */
export function parseTimeInput(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return null;

    let h, m;
    let match = TIME_HHMM_RE.exec(s);
    if (match) {
        h = parseInt(match[1], 10);
        m = parseInt(match[2], 10);
    } else if ((match = TIME_HHONLY_RE.exec(s))) {
        h = parseInt(match[1], 10);
        m = 0;
    } else if ((match = TIME_DIGITS_RE.exec(s))) {
        const digits = match[1].padStart(4, "0");
        h = parseInt(digits.slice(0, 2), 10);
        m = parseInt(digits.slice(2), 10);
    } else {
        return null;
    }
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** True if `raw` normalises to a valid HH:MM. Empty strings return false. */
export function isValidTimeInput(raw) {
    return parseTimeInput(raw) !== null;
}

/**
 * Combine a `YYYY-MM-DD` date + `HH:MM` time into an ISO 8601 UTC
 * string using the BROWSER's local timezone. Callers pass canonical
 * (already parsed / normalised) inputs. Returns `null` if either
 * input is missing / malformed — the caller must gate submission.
 */
export function combineLocalDateTimeToIso(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!dm) return null;
    const tm = TIME_HHMM_RE.exec(timeStr);
    if (!tm) return null;
    const y = parseInt(dm[1], 10);
    const mo = parseInt(dm[2], 10);
    const d = parseInt(dm[3], 10);
    const h = parseInt(tm[1], 10);
    const mi = parseInt(tm[2], 10);
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
    const local = new Date(y, mo - 1, d, h, mi, 0, 0);
    if (Number.isNaN(local.getTime())) return null;
    return local.toISOString();
}

/**
 * Inverse of `combineLocalDateTimeToIso` — split an ISO datetime
 * back into `{date, time}` strings in the BROWSER's local timezone.
 * Empty / invalid input returns `{date: "", time: ""}`.
 */
export function splitIsoToLocalDateTime(iso) {
    if (!iso) return { date: "", time: "" };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    const pad = (n) => String(n).padStart(2, "0");
    return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
}

export const TIME_PLACEHOLDER = "HH:MM";
